import {Empty, Popover, Typography} from "antd";
import {FC} from "react";
import Icon from "../../assets/icons/icon";
import _t from "../../lang/translate";
import MileageDocumentation from "../../screens/forms/tax/admin/mileageDocumentation";
import {Vehicle} from "../../types/appTypes";
import format from "../../utilities/formatNumbers";
import {
  isDATEmissionEqual,
  isDMRBatteryCapacityEqual,
  isDMREmissionEqual,
  isDMREmssionUnitEqual
} from "../../utilities/mapDataFromDMR";
import {isString, isValidNum} from "../../utilities/typeGuard";
import CopyableContent from "../copyableContent";
import Details from "../detailsList";
import {emissionCategoryWarningCheck, EmissionCategoryWarningPopover} from "./editVehicleInstanceData";

const { Link } = Typography;


type Props = {
  vehicle: Vehicle | null;
  showDMRAlerts?: boolean;
};

const VehicleInstanceData: FC<Props> = ({ vehicle, showDMRAlerts, }) => {

  if (!vehicle) {
    return <Empty />;
  }
  const {
    type,
    firstRegDate,
    vin,
    mileage,
    quality,
    useCase,
    emissionAmount,
    emissionUnit,
    batteryCapacity,
    isHighWeight,
    isNew,
    isDanish,
    link,
    batteryWeight
  } = vehicle;

  const formatValue = (v?: unknown | null) => {
    if (isString(v)) {
      return v.trim()
    } else if (isValidNum(v) || typeof v === 'boolean') {
      return v + "";
    } else {
      return "-";
    }
  };

  const dmrEmission = vehicle?.remoteData?.dmr?.mapped?.emissionAmount
  const datEmission = vehicle?.remoteData?.dat?.mapped?.emissionAmount

  const { sameDat, sameDmr } = emissionCategoryWarningCheck({ emissionAmount, datEmission, dmrEmission })

  return (
    <>
      <Details >
        <Details.Title className="mt-0" bold >{_t("taxBasis")}</Details.Title>
        {link && (
          <Details.Item
            label={_t("link")}
            value={
              <Link ellipsis className="max-width-240" href={link} target="_blank">
                {link}
              </Link>
            }
          />
        )}
        <Details.Item label={_t("type")} value={formatValue(type)} />
        <Details.Item label={_t("new", "vehicle") + "?"} value={format.yesOrNo(isNew)} />
        <Details.Item label={_t("is_danish")} value={isDanish == null ? _t("unknown") : format.yesOrNo(isDanish)} />

        <Details.Item
          label={_t("vin")}
          value={
            <CopyableContent contentToCopy={formatValue(vin)}>
              {formatValue(vin)}
            </CopyableContent>
          }
        />
        <Details.Item
          label={_t("firstRegDate")}
          value={
            <CopyableContent contentToCopy={format.date(firstRegDate)}>
              {format.date(firstRegDate)}
            </CopyableContent>
          }
        />
        <MileageDocumentation />
        <Details.Divider><small>Teknisk data</small></Details.Divider>
        <Details.Item
          label={_t("over_3000_kg")}
          value={format.yesOrNo(isHighWeight)}
        />
        <Details.Item label={_t("car_condition")} value={formatValue(quality)} />
        <Details.Item label={_t("special_use")} value={formatValue(useCase)} />
        <Details.Item value={format.emission(emissionAmount)}
          label={
            <span>
              {_t("co2_emission")}
              {showDMRAlerts && !(isDMREmissionEqual(vehicle) || isDATEmissionEqual(vehicle)) && (
                <AlertEmissionData vehicle={vehicle} />
              )}
            </span>
          }
        />
        <Details.Item value={
          <EmissionCategoryWarningPopover datEmission={datEmission} dmrEmission={dmrEmission} emissionAmount={emissionAmount}>
            <span style={{ fontWeight: 600, color: !sameDat || !sameDmr ? "#FF2424" : "#0A1E31" }}>
              {printEmissionCategory(vehicle.emissionAmount)}
            </span>
          </EmissionCategoryWarningPopover>
        }
          label={
            <span>
              CO2-kategori
              {showDMRAlerts && !(isDMREmissionEqual(vehicle) || isDATEmissionEqual(vehicle)) && (
                <AlertEmissionData vehicle={vehicle} />
              )}
            </span>
          }
        />
        <Details.Item value={formatValue(emissionUnit)}
          label={
            <span>
              {_t("co2_measurement")}
              {showDMRAlerts && (!isDMREmssionUnitEqual(vehicle).isEqual || isDMREmssionUnitEqual(vehicle).isDerived) && (
                <AlertDMRData field={_t("co2_measurement")}
                  message={isDMREmssionUnitEqual(vehicle).isDerived ? _t("dmr_data_unclear_on_emission_unit") : undefined}
                />
              )}
            </span>
          }
        />
        <Details.Item
          className="mb-0"
          label={
            <span>
              {_t("battery_capacity")}
              {showDMRAlerts && !isDMRBatteryCapacityEqual(vehicle) && (
                <AlertDMRData field={_t("battery_capacity")} />
              )}
            </span>
          }
          value={format.batteryCapacity(batteryCapacity)}
        />
        <Details.Item
          className="mb-0"
          label={
            <span>
              {_t("battery_weight")}
            </span>
          }
          value={batteryWeight ? format.number(batteryWeight) + " kg" : "-"}
        />
      </Details>
    </>
  );
};

export default VehicleInstanceData;

export function printEmissionCategory(emissionAmount?: number | null) {
  if (!isValidNum(emissionAmount)) return "-"
  if (emissionAmount === 0) return "Elbil (Nul-emission)"
  if (emissionAmount > 0 && emissionAmount < 50) return "Hybrid (Lav-emission)"
  return "Fossil (Høj-emission)"
}
type AlertDMRDataProps = {
  field: string;
  message?: string;
}
const AlertDMRData = ({ field, message }: AlertDMRDataProps) => {
  return (
    <Popover content={
      <>
        <span className="d-block">
          - {_t("dmr_has_differing_data_for")}: {field}
        </span>
        {message && (
          <span className="d-block">- {message}</span>
        )}
      </>
    }>
      <Icon size="large" className="ml-05 no-bg text-warning cursor-pointer" name="alert" />
    </Popover>
  )
}


type AlertEmissionDataProps = {
  vehicle: Vehicle;
}
const AlertEmissionData = ({ vehicle }: AlertEmissionDataProps) => {
  const dmrEmission = vehicle.remoteData?.dmr?.mapped?.emissionAmount
  const datEmission = vehicle.remoteData?.dat?.mapped?.emissionAmount
  return (
    <Popover content={
      <>
        <span className="d-block">
          - {_t("dmr_has_differing_data_for")}: {_t("co2_emission")}
        </span>
        <span className="d-block">DAT Emission: {datEmission}</span>
        <span className="d-block">DMR Emission: {dmrEmission}</span>
      </>
    }>
      <Icon size="large" className="ml-05 no-bg text-warning cursor-pointer" name="alert" />
    </Popover>
  )
}
